(() => {
  const loader = document.currentScript;
  const originalSrc = loader && loader.src
    ? loader.src.replace(/product-form\.js(?:\?.*)?$/, 'product-form-original.js')
    : '';

  const normalize = (value = '') => String(value).replace(/\s+/g, ' ').trim().toLowerCase();

  function getVariantRadios(productForm) {
    const info = productForm.closest('.product__info-container') || document;
    return info.querySelector('variant-radios');
  }

  function getOptionInfo(productForm) {
    const radios = getVariantRadios(productForm);
    if (!radios) return null;

    const fieldsets = Array.from(radios.querySelectorAll('fieldset'));
    if (!fieldsets.length) return null;

    const valuesFor = (fieldset) => Array.from(fieldset.querySelectorAll('input[type="radio"]')).map((input) => normalize(input.value));
    const labelFor = (fieldset) => normalize((fieldset.querySelector('legend') || {}).textContent || '');

    const printIndex = fieldsets.findIndex((fieldset) => {
      const label = labelFor(fieldset);
      const values = valuesFor(fieldset);
      return label.includes('print option') || label === 'finish' || label === 'format' || (values.includes('framed') && values.includes('canvas'));
    });

    const sizeIndex = fieldsets.findIndex((fieldset) => labelFor(fieldset).includes('size'));
    if (printIndex < 0 || sizeIndex < 0 || printIndex === sizeIndex) return null;

    const printFieldset = fieldsets[printIndex];
    const sizeFieldset = fieldsets[sizeIndex];
    const selectedPrint = printFieldset.querySelector('input[type="radio"]:checked');

    return {
      radios,
      printIndex,
      sizeIndex,
      printFieldset,
      sizeFieldset,
      selectedPrintValue: selectedPrint ? selectedPrint.value : ''
    };
  }

  function getSelectedPrintOption(productForm) {
    const info = getOptionInfo(productForm);
    if (info && info.selectedPrintValue) return normalize(info.selectedPrintValue);

    const form = productForm.form || productForm.querySelector('form');
    if (form) {
      const entries = Array.from(new FormData(form).entries());
      for (const [key, value] of entries) {
        if (normalize(key).includes('frame finish')) continue;
        const v = normalize(value);
        if (v === 'print only' || v === 'print-only' || v === 'framed' || v === 'canvas') return v;
      }
    }
    return '';
  }

  function findFrameFinishContainer(productForm) {
    if (typeof productForm.getFrameFinishContainer === 'function') {
      const existing = productForm.getFrameFinishContainer();
      if (existing) return existing;
    }

    const info = productForm.closest('.product__info-container') || document;
    const controls = Array.from(info.querySelectorAll('input, select, textarea'));
    const control = controls.find((el) => {
      const bits = [el.name, el.id, el.getAttribute('aria-label'), el.getAttribute('data-name'), el.getAttribute('data-label')]
        .filter(Boolean).join(' ');
      return normalize(bits).includes('frame finish');
    });
    if (control) return control.closest('.gpo-element, [class*="gpo-element"], fieldset, [class*="globo"]') || control.parentElement;

    const textNodes = Array.from(info.querySelectorAll('label, legend, .gpo-label, [class*="label"], [class*="title"]'));
    const marker = textNodes.find((el) => normalize(el.textContent).includes('frame finish'));
    return marker ? (marker.closest('.gpo-element, [class*="gpo-element"], fieldset, [class*="globo"]') || marker.parentElement) : null;
  }

  function enforceFrameFinish(productForm) {
    const container = findFrameFinishContainer(productForm);
    if (!container) return;

    const show = getSelectedPrintOption(productForm) === 'framed';
    container.style.setProperty('display', show ? '' : 'none', show ? '' : 'important');
    container.setAttribute('aria-hidden', show ? 'false' : 'true');
    container.querySelectorAll('input, select, textarea').forEach((input) => {
      input.disabled = !show;
    });
  }

  function enforceSizeChoices(productForm) {
    const info = getOptionInfo(productForm);
    if (!info) return;

    const json = info.radios.querySelector('script[type="application/json"]');
    if (!json) return;

    let variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }

    const selected = info.selectedPrintValue;
    if (!selected) return;

    const allowed = new Set(
      variants
        .filter((variant) => variant.options && variant.options[info.printIndex] === selected)
        .map((variant) => variant.options[info.sizeIndex])
    );
    if (!allowed.size) return;

    const sizeInputs = Array.from(info.sizeFieldset.querySelectorAll('input[type="radio"]'));
    sizeInputs.forEach((input) => {
      const valid = allowed.has(input.value);
      input.disabled = !valid;
      const label = input.id ? info.sizeFieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
      if (label) {
        label.style.setProperty('display', valid ? '' : 'none', valid ? '' : 'important');
        label.setAttribute('aria-hidden', valid ? 'false' : 'true');
      }
    });

    const checked = sizeInputs.find((input) => input.checked);
    if (!checked || !allowed.has(checked.value)) {
      const firstValid = sizeInputs.find((input) => allowed.has(input.value));
      if (firstValid) {
        firstValid.checked = true;
        firstValid.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  const finishCopy = {
    'print only': `
      <p><strong>Choose a professional photographic print, produced from the high-resolution original with no watermark.</strong></p>
      <ul>
        <li>Professional matt lustre photographic paper</li>
        <li>Careful colour management and attention to detail</li>
        <li>Made to order in the UK</li>
      </ul>
      <p><strong>Free UK delivery · 30-day returns</strong></p>`,
    'framed': `
      <p><strong>Choose a professionally framed piece, made to order in the UK and supplied ready to hang.</strong></p>
      <ul>
        <li>Conservation-quality acid-free mount board</li>
        <li>Premium solid wood open-grain frame</li>
        <li>2mm clear glass</li>
        <li>White, black or oak frame finish</li>
        <li>Framing by a specialist UK supplier who is a member of the <strong>Fine Art Trade Guild</strong></li>
        <li>Ready to hang</li>
      </ul>
      <p><strong>Free UK delivery · 30-day returns</strong></p>`,
    'canvas': `
      <p><strong>Choose a premium canvas print, professionally produced in the UK and supplied ready to hang.</strong></p>
      <ul>
        <li>Premium <strong>340gsm 100% cotton canvas</strong></li>
        <li>Professional <strong>12-colour printing</strong></li>
        <li>Hand-stretched over a substantial <strong>45mm solid-pine frame</strong></li>
        <li>Hand-finished and ready to hang</li>
        <li>Made to order in the UK</li>
      </ul>
      <p><strong>Free UK delivery · 30-day returns</strong></p>`
  };

  function enforceFinishDetails(productForm) {
    const info = productForm.closest('.product__info-container') || document;
    const description = info.querySelector('.product__description.rte');
    if (!description) return;

    if (!description.dataset.wbpOriginalHtml) {
      description.dataset.wbpOriginalHtml = description.innerHTML;
    }

    let selected = getSelectedPrintOption(productForm);
    if (selected === 'print-only') selected = 'print only';

    if (finishCopy[selected]) {
      if (description.dataset.wbpFinish !== selected) {
        description.innerHTML = finishCopy[selected];
        description.dataset.wbpFinish = selected;
      }
    } else if (description.dataset.wbpFinish) {
      description.innerHTML = description.dataset.wbpOriginalHtml;
      delete description.dataset.wbpFinish;
    }
  }

  function applyFixes(productForm) {
    enforceSizeChoices(productForm);
    enforceFrameFinish(productForm);
  }

  function installPatches() {
    const ProductForm = customElements.get('product-form');
    if (!ProductForm) return false;

    const proto = ProductForm.prototype;

    proto.updateFrameFinishVisibility = function() {
      enforceFrameFinish(this);
    };

    proto.cleanFrameFinishProperty = function(formData) {
      if (getSelectedPrintOption(this) === 'framed') return;
      Array.from(formData.keys()).forEach((key) => {
        if (/^properties\[\s*frame finish\s*\]$/i.test(key)) formData.delete(key);
      });
    };

    const runAll = () => document.querySelectorAll('product-form').forEach((form) => applyFixes(form));
    document.addEventListener('change', () => setTimeout(runAll, 0), true);
    document.addEventListener('click', () => setTimeout(runAll, 0), true);
    [0, 50, 150, 400, 900, 1800, 3500].forEach((delay) => setTimeout(runAll, delay));

    const observer = new MutationObserver(() => runAll());
    observer.observe(document.body, { childList: true, subtree: true });
    runAll();
    return true;
  }

  if (!originalSrc) return;
  const script = document.createElement('script');
  script.src = originalSrc;
  script.onload = () => {
    if (!installPatches()) {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (installPatches() || attempts > 20) clearInterval(timer);
      }, 50);
    }
  };
  script.onerror = () => console.error('WBP: product-form original script failed to load');
  document.head.appendChild(script);
})();
